import { computed, onUnmounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';

export const FAVORITE_INBOXES_UPDATED = 'favorite-inboxes:updated';

export const normalizeFavoriteInboxIds = ids =>
  [...new Set((Array.isArray(ids) ? ids : []).map(Number))].filter(
    id => Number.isInteger(id) && id > 0
  );

export const getFavoriteInboxesStorageKey = (userId, accountId) => {
  const normalizedUserId = Number(userId);
  const normalizedAccountId = Number(accountId);
  if (
    !Number.isInteger(normalizedUserId) ||
    normalizedUserId <= 0 ||
    !Number.isInteger(normalizedAccountId) ||
    normalizedAccountId <= 0
  ) {
    return '';
  }
  return `${LOCAL_STORAGE_KEYS.FAVORITE_INBOXES}:${normalizedUserId}:${normalizedAccountId}`;
};

export const readFavoriteInboxIds = storageKey => {
  if (!storageKey) return null;
  const storedIds = LocalStorage.get(storageKey);
  return Array.isArray(storedIds) ? normalizeFavoriteInboxIds(storedIds) : null;
};

export const persistFavoriteInboxIds = (storageKey, ids) => {
  const normalizedIds = normalizeFavoriteInboxIds(ids);
  if (storageKey) {
    try {
      LocalStorage.set(storageKey, normalizedIds);
    } catch {
      // Keep the in-memory preference when localStorage is unavailable.
    }
  }
  return normalizedIds;
};

export function useFavoriteInboxes() {
  const accountId = useMapGetter('getCurrentAccountId');
  const currentUserId = useMapGetter('getCurrentUserID');
  const { uiSettings, updateUISettings } = useUISettings();
  const localFavoriteInboxIds = ref(null);

  const storageKey = computed(() =>
    getFavoriteInboxesStorageKey(currentUserId.value, accountId.value)
  );
  const favoriteInboxesByAccount = computed(() => {
    const settings = uiSettings.value.favorite_inboxes;
    return settings && typeof settings === 'object' && !Array.isArray(settings)
      ? settings
      : {};
  });
  const serverFavoriteInboxIds = computed(() =>
    normalizeFavoriteInboxIds(
      favoriteInboxesByAccount.value[String(accountId.value)]
    )
  );

  watch(
    storageKey,
    key => {
      localFavoriteInboxIds.value = readFavoriteInboxIds(key);
    },
    { immediate: true, flush: 'sync' }
  );
  watch(
    [storageKey, serverFavoriteInboxIds],
    ([key, serverIds]) => {
      if (
        !key ||
        localFavoriteInboxIds.value !== null ||
        serverIds.length === 0
      ) {
        return;
      }
      localFavoriteInboxIds.value = persistFavoriteInboxIds(key, serverIds);
    },
    { immediate: true, flush: 'sync' }
  );

  const favoriteInboxIds = computed(
    () => localFavoriteInboxIds.value ?? serverFavoriteInboxIds.value
  );
  const syncFavoriteInboxIds = ({ key, ids }) => {
    if (key !== storageKey.value) return;
    localFavoriteInboxIds.value = normalizeFavoriteInboxIds(ids);
  };
  emitter.on(FAVORITE_INBOXES_UPDATED, syncFavoriteInboxIds);
  onUnmounted(() =>
    emitter.off(FAVORITE_INBOXES_UPDATED, syncFavoriteInboxIds)
  );

  const isInboxFavorite = inboxId =>
    favoriteInboxIds.value.includes(Number(inboxId));
  const toggleFavoriteInbox = inboxId => {
    const id = Number(inboxId);
    if (!Number.isInteger(id) || id <= 0) return false;
    const currentIds = favoriteInboxIds.value;
    const isFavorite = currentIds.includes(id);
    const nextIds = persistFavoriteInboxIds(
      storageKey.value,
      isFavorite
        ? currentIds.filter(currentId => currentId !== id)
        : [...currentIds, id]
    );
    localFavoriteInboxIds.value = nextIds;
    emitter.emit(FAVORITE_INBOXES_UPDATED, {
      key: storageKey.value,
      ids: nextIds,
    });
    updateUISettings({
      favorite_inboxes: {
        ...favoriteInboxesByAccount.value,
        [String(accountId.value)]: nextIds,
      },
    });
    return !isFavorite;
  };

  return { favoriteInboxIds, isInboxFavorite, toggleFavoriteInbox };
}
